import React, { useState, useEffect } from 'react';
import { AuthService } from '@/core/application/security/AuthService';
import { useNavigate } from 'react-router-dom';
import { DynamicLoader } from '@/shared/components/DynamicLoader';
import { Activity, Fingerprint, Mail, KeyRound, ArrowLeft } from 'lucide-react';

type AuthMode = 'initial' | 'email' | 'otp';

export const LoginScreen = () => {
  const navigate = useNavigate();
  const [authMode, setAuthMode] = useState<AuthMode>('initial');
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  
  const [isLoadingGoogle, setIsLoadingGoogle] = useState(false);
  const [isLoadingPasskey, setIsLoadingPasskey] = useState(false);
  const [isLoadingOtp, setIsLoadingOtp] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleGoogleLogin = async () => {
    setIsLoadingGoogle(true);
    try {
      await AuthService.signInWithGoogle();
    } catch (error: any) {
      globalThis.alert(error.message || 'No se pudo iniciar sesión con Google.');
    } finally {
      setIsLoadingGoogle(false);
    }
  };

  const handlePasskeyLogin = async () => {
    setIsLoadingPasskey(true);
    try {
      await AuthService.signInWithPasskey();
    } catch (error: any) {
      globalThis.alert(error.message || 'Error al usar Passkey o no soportado en tu dispositivo.');
    } finally {
      setIsLoadingPasskey(false);
    }
  };

  const handleSendOtp = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email) return;
    setIsLoadingOtp(true);
    try {
      await AuthService.signInWithOtp(email);
      setAuthMode('otp');
    } catch (error: any) {
      globalThis.alert(error.message || 'Error al enviar código. Intenta de nuevo.');
    } finally {
      setIsLoadingOtp(false);
    }
  };

  const handleVerifyOtp = async (e: React.SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!otpCode || otpCode.length < 6) return;
    setIsLoadingOtp(true);
    try {
      await AuthService.verifyOtp(email, otpCode);
      // Supabase redireccionará o cambiará el estado de sesión automáticamente
    } catch (error: any) {
      globalThis.alert(error.message || 'Código incorrecto o expirado.');
    } finally {
      setIsLoadingOtp(false);
    }
  };

  const renderAuthContent = () => {
    if (isLoadingGoogle || isLoadingPasskey || isLoadingOtp) {
      let loadingMessages: string[] = [];
      
      if (isLoadingOtp) {
        loadingMessages = [
          "Enviando correo seguro...",
          "Generando código encriptado...",
          "Verificando identidad..."
        ];
      } else if (isLoadingGoogle) {
        loadingMessages = [
          "Validando identidad con Google...",
          "Sincronizando tu bóveda de salud...",
          "Preparando entorno clínico..."
        ];
      } else {
        loadingMessages = [
          "Validando biometría local...",
          "Asegurando tu identidad clínica...",
          "Desencriptando bóveda personal..."
        ];
      }

      return (
        <div className="py-10 flex justify-center">
          <DynamicLoader 
            messages={loadingMessages}
            color="#0284C7"
          />
        </div>
      );
    }

    if (authMode === 'initial') {
      return (
        <div className="flex flex-col">
          <button 
            onClick={handleGoogleLogin}
            className="w-full h-14 bg-white text-slate-900 rounded-xl flex items-center justify-center gap-3 border border-slate-200 cursor-pointer text-base font-bold shadow-sm transition-colors hover:bg-slate-50"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continuar con Google
          </button>

          <button 
            onClick={() => setAuthMode('email')}
            className="w-full h-14 bg-white text-slate-900 rounded-xl flex items-center justify-center gap-3 border border-slate-200 cursor-pointer text-base font-bold shadow-sm transition-colors hover:bg-slate-50 mt-3"
          >
            <Mail size={22} className="text-slate-900" />
            Continuar con Correo
          </button>

          <div className="flex items-center my-6">
            <div className="flex-1 h-px bg-slate-200" />
            <span className="mx-3 text-slate-500 text-sm font-medium">
              o la forma más rápida
            </span>
            <div className="flex-1 h-px bg-slate-200" />
          </div>

          <button 
            onClick={handlePasskeyLogin}
            className="w-full h-14 bg-sky-600 text-white rounded-xl flex items-center justify-center gap-3 border-none cursor-pointer text-base font-bold shadow-[0_6px_10px_rgba(2,132,199,0.2)] transition-opacity hover:opacity-90"
          >
            <Fingerprint size={24} className="text-white" />
            Ingresar con Passkey
          </button>

          <button 
            onClick={() => navigate(-1)}
            className="mt-5 bg-transparent border-none p-2 text-slate-500 text-sm font-semibold cursor-pointer text-decoration-none hover:underline mx-auto block"
          >
            Cancelar y volver al inicio
          </button>
        </div>
      );
    }

    if (authMode === 'email') {
      return (
        <form onSubmit={handleSendOtp} className="flex flex-col">
          <button 
            type="button" 
            onClick={() => setAuthMode('initial')} 
            className="bg-transparent border-none text-slate-500 flex items-center gap-1.5 text-sm font-semibold cursor-pointer mb-5 p-0 self-start hover:text-slate-700 transition-colors"
          >
            <ArrowLeft size={16} /> Volver
          </button>
          
          <h3 className="m-0 mb-2 text-slate-900 text-lg">Ingresa con tu Correo</h3>
          <p className="m-0 mb-5 text-slate-500 text-sm leading-5">
            Te enviaremos un código de seguridad de 6 dígitos. No necesitas contraseña.
          </p>

          <input
            type="email"
            placeholder="tu@correo.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full h-14 px-4 rounded-xl border border-slate-300 text-base mb-5 outline-none text-slate-900 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 transition-shadow"
          />

          <button 
            type="submit"
            className="w-full h-14 bg-sky-600 text-white rounded-xl border-none cursor-pointer text-base font-bold shadow-[0_6px_10px_rgba(2,132,199,0.2)] transition-opacity hover:opacity-90"
          >
            Enviar Código
          </button>
        </form>
      );
    }

    return (
      <form onSubmit={handleVerifyOtp} className="flex flex-col">
        <button 
          type="button" 
          onClick={() => setAuthMode('email')} 
          className="bg-transparent border-none text-slate-500 flex items-center gap-1.5 text-sm font-semibold cursor-pointer mb-5 p-0 self-start hover:text-slate-700 transition-colors"
        >
          <ArrowLeft size={16} /> Volver
        </button>

        <div className="flex justify-center mb-4">
          <KeyRound size={40} className="text-sky-600" />
        </div>
        <h3 className="m-0 mb-2 text-slate-900 text-lg text-center">Ingresa el Código</h3>
        <p className="m-0 mb-5 text-slate-500 text-sm leading-5 text-center">
          Revisa la bandeja de <strong>{email}</strong>
        </p>

        <input
          type="text"
          placeholder="000000"
          value={otpCode}
          onChange={(e) => setOtpCode(e.target.value)}
          maxLength={6}
          required
          className="w-full h-14 px-4 rounded-xl border border-slate-300 text-2xl mb-5 outline-none text-slate-900 text-center tracking-[4px] font-bold focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-shadow"
        />

        <button 
          type="submit"
          className="w-full h-14 bg-emerald-600 text-white rounded-xl border-none cursor-pointer text-base font-bold shadow-[0_6px_10px_rgba(5,150,105,0.2)] transition-opacity hover:opacity-90"
        >
          Verificar Código
        </button>
      </form>
    );
  };

  return (
    <div className="flex-1 bg-slate-50 flex flex-col justify-center p-6 overflow-hidden relative min-h-screen">
      {/* Decorative Background Glows */}
      <div className="absolute top-[-150px] left-[-50px] w-screen h-[100vw] bg-sky-600/5 rounded-full z-0 pointer-events-none" />
      <div className="absolute bottom-[-100px] right-[-100px] w-[80vw] h-[80vw] bg-emerald-600/5 rounded-full z-0 pointer-events-none" />

      <div 
        className="w-full max-w-md mx-auto z-10 transition-all duration-700 ease-out"
        style={{
          opacity: mounted ? 1 : 0, 
          transform: mounted ? 'translateY(0)' : 'translateY(20px)',
        }}
      >
        
        {/* Brand Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center border border-sky-100 mb-4 shadow-sm">
            <Activity size={32} className="text-sky-600" />
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 text-center tracking-tight m-0 mb-2">
            Aura Clínico
          </h1>
          <p className="text-base text-slate-600 text-center m-0">
            Acceso seguro a tu red profesional
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_10px_20px_rgba(100,116,139,0.06)] border border-slate-100 mb-6 transition-all duration-300 ease-in-out">
          {renderAuthContent()}
        </div>

        {/* Footer info */}
        <p className="text-xs text-slate-400 text-center px-5 leading-relaxed m-0">
          Al ingresar aceptas nuestras políticas de privacidad clínica y encriptación de datos.
        </p>
      </div>
    </div>
  );
};
